import Navbar from "components/Navbar";
import React from "react";

import { AssetTypes } from "common/utils/types";
import { CameraAlt } from "@material-ui/icons";
import { CommonLinearProgress } from "common/components/CommonLinearProgress";
import { ContentWrapper } from "components/ContentWrapper";
import { formatDateDifference } from "common/utils/utility";
import { Property } from "../../common/store/property/types";
import { StoreState } from "../../common/store/types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import {
  Box,
  Button,
  Grid,
  Typography,
  withWidth,
  isWidthDown,
  Paper,
} from "@material-ui/core";

import "./PropertyDetail.scss";

const PropertyInfo = ({ property, className }: any) => {
  const nFormatter = (num: number) => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1).replace(/\.0$/, "") + "G";
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "m";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    }
    return num;
  };

  const history = useHistory();
  const { t } = useTranslation();

  const onPurchaseShares = () => {
    history.push(`/shares/${property.id}`);
  };

  const duration = formatDateDifference(property.end, property.start);

  return (
    <Grid container className={className} spacing={4}>
      <Grid item container>
        {property.token.cost ? (
          <>
            <Grid item xs={6}>
              <Typography variant="body1" color="primary" className="item-text">
                {t("Cost")}:
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body1"
                color="primary"
                className="item-value"
              >
                €{nFormatter(Number(property.token.cost))}
              </Typography>
            </Grid>
          </>
        ) : null}

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-text">
            {t("Value")}:
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-value">
            €{nFormatter(Number(property.token.value))}
          </Typography>
        </Grid>

        {property.token.asset_type === AssetTypes.LOAN ? (
          <>
            <Grid item xs={6}>
              <Typography className="item-text">{t("LTV")}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography align="right" className="item-value">
                {property.token.LTV}%
              </Typography>
            </Grid>
          </>
        ) : null}

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-text">
            {t("Shares Sold")}:
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-value">
            €{nFormatter(Number(property.total_sold))}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-text">
            {t("Shares Available")}:
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-value">
            €{nFormatter(Number(property.cap) - Number(property.total_sold))}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-text">
            {t("Price/Share")}:
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body1" color="primary" className="item-value">
            €{nFormatter(Number(property.price))}
          </Typography>
        </Grid>

        {property.token.asset_type === AssetTypes.LOAN ? (
          <>
            <Grid item xs={6}>
              <Typography className="item-text">{t("Interest")}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" className="item-value">
                {property.token.interest}%
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography className="item-text">{t("Duration")}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" className="item-value">
                {duration}
              </Typography>
            </Grid>
          </>
        ) : null}
      </Grid>

      <Grid item xs={12}>
        <Button
          className="primary"
          variant="contained"
          color="primary"
          fullWidth
          onClick={onPurchaseShares}
        >
          {t("Purchase shares")}
        </Button>
      </Grid>
    </Grid>
  );
};

function PropertyDetail({ width }: any) {
  //Hooks
  const { id } = useParams<{ id: string }>();
  const property = useSelector<StoreState, Property | any>((state) =>
    state.properties.find((p) => p.id === id)
  );
  const { t } = useTranslation();

  return (
    <Box>
      <Box className="cover">
        <Navbar />

        <Box className="cover-content">
          <Typography variant="h4" className="location">
            {property.token.asset_type === AssetTypes.REAL_ESTATE
              ? property.token.location
              : null}
          </Typography>
          <Typography variant="h2" color="secondary" className="property-title">
            {property.token.name}
          </Typography>

          <Button
            color="primary"
            variant="contained"
            className="primary"
            startIcon={<CameraAlt />}
          >
            {t("View photos")}
          </Button>
        </Box>
      </Box>

      {property && (
        <ContentWrapper justifyContent="left" padding="50px 11% 50px 11%">
          <Box
            width="675px"
            marginTop={isWidthDown("md", width) ? "300px" : "0px"}
            className="property-details"
          >
            <Grid container item spacing={0} xs={12}>
              <Grid item container spacing={isWidthDown("xs", width) ? 2 : 9}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="info-text"
                  >
                    {t("Fixed interest rate")}
                  </Typography>

                  <Typography
                    variant="h2"
                    color="textSecondary"
                    className="info-value"
                  >
                    5.0%
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="info-text"
                  >
                    {t("Running time")}
                  </Typography>

                  <Typography
                    variant="h2"
                    color="textSecondary"
                    className="info-value"
                  >
                    26 - 32 {t("months")}
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Paper className="spacing" />
              </Grid>

              <Grid item xs={12}>
                <CommonLinearProgress
                  label={t("Funded")}
                  value={(property.total_sold * 100) / property.cap}
                />
              </Grid>
            </Grid>
          </Box>
        </ContentWrapper>
      )}

      {property && (
        <PropertyInfo property={property} className="property-info" />
      )}
    </Box>
  );
}

export default withWidth()(PropertyDetail);
